import { SafePromise } from '@benzinga/safe-await';
import { IncludeHeader, RestfulClient, Session } from '@benzinga/session';
import { RestfulStory, NewsBZQLRequestQuery, StoryCategory, Assets } from '../entities';
import { AdvancedNewsEnvironment } from '../environment';

// cloudflared access login https://content-server.prod.api.benzinga.dev
// cloudflared access token -app=https://content-server.prod.api.benzinga.dev
// cloudflared access tcp --hostname content-server.prod.api.benzinga.dev --url localhost:9000

const PUBLIC_NEWS_URL = 'https://api.benzinga.com/api/v2/news';

const PRODUCTION_NEWS_RESILIENCE = {
  delayMultiple: 200,
  delayOffset: 3000,
  isError: undefined,
  maxDelay: 60000,
  maxNumOfAttempts: 3,
  retryOnError: true,
};

const CLOUDFLARE_ACCESS_RESILIENCE = {
  delayMultiple: 0,
  delayOffset: 2500,
  isError: undefined,
  maxDelay: 2500,
  maxNumOfAttempts: 1,
  retryOnError: true,
};

type PublicNewsItem = {
  author?: string;
  channels?: { name?: string }[];
  created?: string;
  id?: number;
  image?: Array<{ url?: string } | string>;
  stocks?: { name?: string }[];
  tags?: { name?: string }[];
  teaser?: string;
  title?: string;
  updated?: string;
  url?: string;
};

const toCategory = (name: string, index: number): StoryCategory => ({
  description: '',
  name,
  primary: index === 0,
  tid: 0,
  vid: 0,
});

const collectBzqlFieldValues = (node: unknown, field: string): string[] => {
  const values: string[] = [];
  const visit = (current: unknown): void => {
    if (Array.isArray(current)) {
      current.forEach(visit);
      return;
    }
    if (!current || typeof current !== 'object') {
      return;
    }
    const record = current as Record<string, unknown>;
    const expression = record['$'];
    if (Array.isArray(expression) && expression[0] === field && Array.isArray(expression[2])) {
      values.push(...expression[2].map(String));
    }
    Object.values(record).forEach(visit);
  };
  visit(node);
  return [...new Set(values)];
};

const toAssets = (image: PublicNewsItem['image']): Assets[] => {
  if (!Array.isArray(image)) {
    return [];
  }
  return image.flatMap((entry, index) => {
    const url = typeof entry === 'string' ? entry : entry?.url;
    if (!url) {
      return [];
    }
    return [{ copyright: '', mime: '', primary: index === 0, title: '', type: 'image', url }];
  });
};

const toRestfulStory = (item: PublicNewsItem): RestfulStory | null => {
  if (item.id == null || !item.created) {
    return null;
  }
  const createdAt = new Date(item.created).toISOString();
  const updatedAt = new Date(item.updated || item.created).toISOString();
  let canonicalPath = '';
  try {
    canonicalPath = item.url ? new URL(item.url).pathname.replace(/^\//, '') : '';
  } catch {
    canonicalPath = '';
  }
  return {
    CanonicalPath: canonicalPath,
    Channels: (item.channels ?? [])
      .map(channel => channel.name ?? '')
      .filter(Boolean)
      .map((name, index) => toCategory(name, index)),
    CreatedAt: createdAt,
    ID: String(item.id),
    IsBzPost: true,
    NodeID: item.id,
    Tags: (item.tags ?? [])
      .map(tag => tag.name ?? '')
      .filter(Boolean)
      .map((name, index) => toCategory(name, index)),
    TeaserText: item.teaser ?? '',
    Tickers: (item.stocks ?? [])
      .map(stock => stock.name ?? '')
      .filter(Boolean)
      .map((name, index) => toCategory(name, index)),
    Title: item.title ?? '',
    UpdatedAt: updatedAt,
    VersionID: 1,
    assets: toAssets(item.image),
    name: item.author ?? null,
  };
};

export class NewsInternalContentRestful extends RestfulClient {
  constructor(session: Session, includeHeaders?: IncludeHeader) {
    super(session.getEnvironment(AdvancedNewsEnvironment).internalContentUrl, session, includeHeaders);
  }

  public isCloudflareAccessHost = (): boolean => {
    const host = this.hostname.host;
    return host === 'cloudflared-api.benzinga.com' || host.endsWith('.api.benzinga.dev');
  };

  public isCloudflareApi = (): boolean => this.isCloudflareAccessHost();

  private getStoriesFromPublicNews = async (
    query: Partial<NewsBZQLRequestQuery>,
  ): SafePromise<{ data: RestfulStory[] }> => {
    const token = this.session.getEnvironment(AdvancedNewsEnvironment).contentKey;
    const url = new URL(PUBLIC_NEWS_URL);
    url.searchParams.set('token', token);
    url.searchParams.set('displayOutput', 'abstract');
    url.searchParams.set('page', '0');
    url.searchParams.set('pageSize', String(Math.min(query.limit ?? 10, 50)));

    const channels = [
      ...collectBzqlFieldValues(query.where, 'Channels.name'),
      ...collectBzqlFieldValues(query.where, 'Channels.tid'),
    ];
    if (channels.length) {
      url.searchParams.set('channels', channels.join(','));
    }
    const tags = collectBzqlFieldValues(query.where, 'Tags.name').filter(tag => !/^\d+$/.test(tag));
    if (tags.length) {
      url.searchParams.set('tags', tags.join(','));
    }
    const tickers = collectBzqlFieldValues(query.where, 'Tickers.name').map(ticker => ticker.replace(/^\$/, ''));
    if (tickers.length) {
      url.searchParams.set('tickers', tickers.join(','));
    }

    const response = await this.get<PublicNewsItem[]>(url, {
      allowsAnonymousAuth: true,
      credentials: 'omit',
      resilience: PRODUCTION_NEWS_RESILIENCE,
    });
    if (response.err) {
      return response;
    }
    if (!Array.isArray(response.ok)) {
      return { ok: { data: [] } };
    }
    return {
      ok: {
        data: response.ok.map(toRestfulStory).filter((story): story is RestfulStory => story != null),
      },
    };
  };

  public getStories = async (query: Partial<NewsBZQLRequestQuery>): SafePromise<{ data: RestfulStory[] }> => {
    const url = this.URL(`/api/v3/internal/news/advanced`);
    const useCloudflareAccess = this.isCloudflareAccessHost();
    const response = await this.post<{ data: RestfulStory[] }, Partial<NewsBZQLRequestQuery>>(url, query, {
      allowsAnonymousAuth: true,
      credentials: useCloudflareAccess ? 'omit' : undefined,
      headers: useCloudflareAccess
        ? {
            'CF-Access-Client-Id': this.session.getEnvironment(AdvancedNewsEnvironment)['CF-Access-Client-Id'],
            'CF-Access-Client-Secret': this.session.getEnvironment(AdvancedNewsEnvironment)['CF-Access-Client-Secret'],
          }
        : {},
      resilience:
        useCloudflareAccess && process.env.NODE_ENV === 'development'
          ? CLOUDFLARE_ACCESS_RESILIENCE
          : PRODUCTION_NEWS_RESILIENCE,
    });

    if (response.ok) {
      return response;
    }
    if (process.env.NODE_ENV === 'development') {
      const fallback = await this.getStoriesFromPublicNews(query);
      if (fallback.ok) {
        return fallback;
      }
    }
    return response;
  };
}
